// External-managed site management panel for SiteDetailPage
// (added 2026-06-02 Phase 2). Shows + manages:
//   - target keywords (add/remove)
//   - competitors (add/remove)
//   - publish endpoint config
//   - test-push button
//   - push history (blog posts + status)

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { externalApi } from "../lib/api";

export function ExternalSitePanel({
  siteId, domain, publishUrl, publishSecretSet, defaultProbeLocation,
}: {
  siteId: string;
  domain: string;
  publishUrl: string | null;
  publishSecretSet: boolean;
  defaultProbeLocation: string | null;
}) {
  return (
    <section className="bg-white rounded-xl border border-ink-100 mb-6 overflow-hidden">
      <header className="px-5 py-4 border-b border-ink-100 flex items-center gap-3">
        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-purple-100 text-purple-800 border border-purple-200">EXTERNAL</span>
        <h2 className="text-lg font-semibold">Managed externally — push pipeline</h2>
      </header>
      <div className="p-5 space-y-6">
        <EndpointPanel siteId={siteId} publishUrl={publishUrl} publishSecretSet={publishSecretSet} defaultProbeLocation={defaultProbeLocation} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KeywordsPanel siteId={siteId} />
          <CompetitorsPanel siteId={siteId} />
        </div>
        <PushHistoryPanel siteId={siteId} domain={domain} />
      </div>
    </section>
  );
}

function EndpointPanel({ siteId, publishUrl, publishSecretSet, defaultProbeLocation }: { siteId: string; publishUrl: string | null; publishSecretSet: boolean; defaultProbeLocation: string | null }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(publishUrl ?? "");
  const [secret, setSecret] = useState("");
  const [location, setLocation] = useState(defaultProbeLocation ?? "United States");
  const update = useMutation({
    mutationFn: () => externalApi.updateEndpoint(siteId, {
      publishUrl: url || undefined,
      publishSecret: secret || undefined,
      defaultProbeLocation: location || undefined,
    }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ["site", siteId] }); },
  });
  const test = useMutation({
    mutationFn: () => externalApi.testPush(siteId),
  });
  return (
    <div className="border border-ink-100 rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">Push endpoint</h3>
        <div className="flex gap-2">
          <button onClick={() => test.mutate()} disabled={!publishUrl || !publishSecretSet || test.isPending} className="text-xs px-2 py-1 border border-ink-200 rounded hover:bg-ink-50 disabled:opacity-40">
            {test.isPending ? "Testing…" : "Test push"}
          </button>
          <button onClick={() => setEditing(!editing)} className="text-xs px-2 py-1 border border-ink-200 rounded hover:bg-ink-50">
            {editing ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <label className="text-xs text-ink-500 block">Publish URL
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full mt-1 px-2 py-1 text-sm border border-ink-200 rounded" placeholder="https://example.com/api/rankforge-content" />
          </label>
          <label className="text-xs text-ink-500 block">Publish secret (leave blank to keep)
            <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className="w-full mt-1 px-2 py-1 text-sm border border-ink-200 rounded" placeholder="32+ char random string" />
          </label>
          <label className="text-xs text-ink-500 block">Default probe location
            <input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full mt-1 px-2 py-1 text-sm border border-ink-200 rounded" placeholder="United States" />
          </label>
          <button onClick={() => update.mutate()} disabled={update.isPending} className="text-xs px-3 py-1 bg-ink-900 text-white rounded">{update.isPending ? "Saving…" : "Save"}</button>
        </div>
      ) : (
        <div className="text-sm space-y-1">
          <Kv k="URL" v={publishUrl ? <code className="text-xs">{publishUrl}</code> : <span className="text-rose-600">not set</span>} />
          <Kv k="Secret" v={publishSecretSet ? <span className="text-green-700">✓ set</span> : <span className="text-rose-600">not set</span>} />
          <Kv k="Location" v={defaultProbeLocation ?? "United States"} />
        </div>
      )}
      {test.data && (
        <div className={`mt-2 p-2 rounded text-xs ${test.data.ok ? "bg-green-50 border border-green-200 text-green-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
          {test.data.ok ? `✓ Test push succeeded (HTTP ${test.data.status}). ${test.data.url ? `Live URL: ${test.data.url}` : ""}` : `✗ Failed: ${test.data.error ?? "HTTP " + test.data.status}`}
        </div>
      )}
    </div>
  );
}

function KeywordsPanel({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["external", siteId, "keywords"], queryFn: () => externalApi.keywords.list(siteId) });
  const [kw, setKw] = useState("");
  const [tag, setTag] = useState("");
  const [loc, setLoc] = useState("");
  const add = useMutation({
    mutationFn: () => externalApi.keywords.add(siteId, { keyword: kw, tag: tag || undefined, locationName: loc || undefined }),
    onSuccess: () => { setKw(""); setTag(""); setLoc(""); qc.invalidateQueries({ queryKey: ["external", siteId, "keywords"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => externalApi.keywords.remove(siteId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["external", siteId, "keywords"] }),
  });
  return (
    <div className="border border-ink-100 rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">Target keywords <span className="text-xs text-ink-400 font-normal">— Spencer probes these</span></h3>
      <ul className="space-y-1 mb-3 max-h-64 overflow-y-auto">
        {q.data?.keywords.map((k) => (
          <li key={k.id} className="flex items-center gap-2 text-sm">
            {k.tag && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{k.tag}</span>}
            <span className="flex-1">{k.keyword}</span>
            {k.locationName && <span className="text-xs text-ink-400">@ {k.locationName}</span>}
            <button onClick={() => remove.mutate(k.id)} className="text-xs text-ink-400 hover:text-rose-600">✕</button>
          </li>
        ))}
        {q.data && q.data.keywords.length === 0 && <li className="text-xs text-ink-500">No keywords yet.</li>}
      </ul>
      <form onSubmit={(e) => { e.preventDefault(); if (kw) add.mutate(); }} className="space-y-2">
        <input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="keyword to track" className="w-full px-2 py-1 text-sm border border-ink-200 rounded" />
        <div className="flex gap-2">
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="tag (optional)" className="flex-1 px-2 py-1 text-xs border border-ink-200 rounded" />
          <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="location override (optional)" className="flex-1 px-2 py-1 text-xs border border-ink-200 rounded" />
          <button type="submit" disabled={!kw || add.isPending} className="text-xs px-3 py-1 bg-ink-900 text-white rounded disabled:opacity-40">Add</button>
        </div>
      </form>
    </div>
  );
}

function CompetitorsPanel({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["external", siteId, "competitors"], queryFn: () => externalApi.competitors.list(siteId) });
  const [c, setC] = useState("");
  const [notes, setNotes] = useState("");
  const add = useMutation({
    mutationFn: () => externalApi.competitors.add(siteId, { competitorDomain: c, notes: notes || undefined }),
    onSuccess: () => { setC(""); setNotes(""); qc.invalidateQueries({ queryKey: ["external", siteId, "competitors"] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => externalApi.competitors.remove(siteId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["external", siteId, "competitors"] }),
  });
  return (
    <div className="border border-ink-100 rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">Competitors <span className="text-xs text-ink-400 font-normal">— Sherlock seeds discovery</span></h3>
      <ul className="space-y-1 mb-3 max-h-64 overflow-y-auto">
        {q.data?.competitors.map((co) => (
          <li key={co.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">{co.competitorDomain}</span>
            {co.notes && <span className="text-xs text-ink-400 italic">{co.notes}</span>}
            <button onClick={() => remove.mutate(co.id)} className="text-xs text-ink-400 hover:text-rose-600">✕</button>
          </li>
        ))}
        {q.data && q.data.competitors.length === 0 && <li className="text-xs text-ink-500">No competitors yet.</li>}
      </ul>
      <form onSubmit={(e) => { e.preventDefault(); if (c) add.mutate(); }} className="space-y-2">
        <input value={c} onChange={(e) => setC(e.target.value)} placeholder="competitor.com" className="w-full px-2 py-1 text-sm border border-ink-200 rounded" />
        <div className="flex gap-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="notes (optional)" className="flex-1 px-2 py-1 text-xs border border-ink-200 rounded" />
          <button type="submit" disabled={!c || add.isPending} className="text-xs px-3 py-1 bg-ink-900 text-white rounded disabled:opacity-40">Add</button>
        </div>
      </form>
    </div>
  );
}

function PushHistoryPanel({ siteId, domain }: { siteId: string; domain: string }) {
  const q = useQuery({
    queryKey: ["external", siteId, "push-history"],
    queryFn: () => externalApi.pushHistory(siteId),
    refetchInterval: 30_000,
  });
  return (
    <div className="border border-ink-100 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-ink-100 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold">Blog post push history</h3>
        <span className="text-xs text-ink-500">{q.data?.posts.length ?? 0} posts</span>
      </div>
      {q.isLoading && <div className="p-4 text-xs text-ink-500">Loading…</div>}
      {q.data && q.data.posts.length === 0 && (
        <div className="p-4 text-xs text-ink-500">
          No posts yet. Run <code className="bg-ink-100 px-1 rounded">rankforge blog-write-bulk --include {domain} --posts-per-site 3 --commit</code> to draft 3 posts, then push from this page or via <code className="bg-ink-100 px-1 rounded">rankforge external push --domain {domain}</code>.
        </div>
      )}
      {q.data && q.data.posts.length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-xs uppercase text-ink-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Title</th>
              <th className="text-left px-3 py-2 font-medium">Keyword</th>
              <th className="text-right px-3 py-2 font-medium">Attempts</th>
              <th className="text-left px-3 py-2 font-medium">Live URL</th>
            </tr>
          </thead>
          <tbody>
            {q.data.posts.map((p) => (
              <tr key={p.id} className="border-t border-ink-100">
                <td className="px-3 py-2">
                  {p.publishedExternallyAt ? (
                    <span className="inline-block text-[10px] uppercase px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-200">published</span>
                  ) : p.externalPushAttempts > 0 ? (
                    <span className="inline-block text-[10px] uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200" title={p.externalPushLastError ?? ""}>failed</span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase px-1.5 py-0.5 rounded bg-ink-100 text-ink-700 border border-ink-200">queued</span>
                  )}
                </td>
                <td className="px-3 py-2">{p.title}</td>
                <td className="px-3 py-2 text-xs text-ink-500">{p.keyword ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{p.externalPushAttempts}</td>
                <td className="px-3 py-2 text-xs">
                  {p.externalPostUrl
                    ? <a href={p.externalPostUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View ↗</a>
                    : <span className="text-ink-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Kv({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="text-ink-500 w-20">{k}:</span>
      <span className="text-ink-900">{v}</span>
    </div>
  );
}
